import React, { useState, useEffect } from 'react';

import PropTypes from 'prop-types';
import SanitizedHTML from 'react-sanitized-html';

import { connect } from 'react-redux';
import { Form, Formik } from 'formik';
import { startOfToday } from 'date-fns';
import { FormattedNumber } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { SubHeader, PageHeader } from 'cstar-react-primitives/lib/formik';
import {
    TabList,
    FieldLayoutSmall,
    ButtonErrorMessage,
    ButtonDisplayErrors
} from 'cstar-react-primitives';

import PromoBlock from '../../promoBlock/PromoBlock';
import { useUsers } from '../../../user/UserContext';
import { isVowel } from '../../../utils/sharedFunctions';
import { useFormatMessage } from '../../../intl/intlHooks';
import { FaceplateImg } from '../../faceplates/FaceplateImg';
import { getAddressHash } from '../../../address/addressUtils';
import { saveLineItem } from '../../../lineItem/lineItemModule';
import { hashedAddresses } from '../../../address/addressSelectors';
import { selectLineItems } from '../../../lineItem/lineItemSelectors';
import { useSegment } from '../../../segment/SegmentProvider';
import { ELECTRONIC_DELIVERY, PHYSICAL_DELIVERY } from '../../../products/productsModule';
import { ShippingRatesProvider, useShippingRates } from '../../../mapi/ShippingRatesProvider';

import { summaryPath } from '../../paths/utils';
import { cashbotName, DeliveryMethodSelection } from '../../core';
import { useSettings } from '../../../mall_settings/SettingsProvider';
import { FaceplateCarousel } from '../../faceplates/FaceplateCarousel';

import { validationMessages } from '../messages/validationMessages';
import { fieldNames, AmountField, IS_SELF_BUY, DELIVERY_DATE_FIELD } from '../fields';

import { DigitalCardForm } from './DigitalCardForm';
import { PhysicalCardForm } from './PhysicalCardForm';
import { getValidationSchema } from './giftCardFormValidation';
import { personalizationFormMessages } from './giftCardMessages';
import { getConstrainedAmount, getDefaultDenomination } from './utils';
import { QuantityPicker } from '../fields/QuantityPicker';

import CARD_FEE_IMAGE from "./images/card-fee-icon.inline.svg";
import GiftCardDetails from './GiftCardDetails';

export const InnerGiftCardForm = ({
    values,
    errors,
    touched,
    setTouched,
    setFieldValue,
    resetForm,
    initialValues,
    getHashedAddresses,
    submitForm
}) => {
    const { productClicked, productViewed } = useSegment();
    const { defaultDenomination, userPoints, displayProductTitle } = useSettings();
    const { product = {} } = values;
    const { description, redemptionText, termsAndConditions } = product;
    const initialResetValues = values.id === undefined ? initialValues : values;
    const formatMessage = useFormatMessage();
    const { setAmountUsed } = useUsers();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [selectedFaceplate, setSelectedFaceplate] = useState(
        values.faceplateCode
            ? values.product.faceplates.find((fp) => fp.code === values.faceplateCode)
            : values.product.faceplates[0]
    );
    const [deliveryMethod, setDeliveryMethod] = useState(values.product.deliveryMethod);

    const headerMessage =
        values.deliveryMethod === ELECTRONIC_DELIVERY
            ? formatMessage(personalizationFormMessages.whoForHeader)
            : formatMessage(personalizationFormMessages.wheresItGoingHeader);

    const { shippingMethods, doFetch, error, numFetches } = useShippingRates();
    const { canShip, ...newTouched } = touched;
    const anyTouched = Object.keys(newTouched).length > 0;
    const showErrorMsg = Object.keys(newTouched).reduce(
        (hasTouchedErrors, field) => hasTouchedErrors || Object.keys(errors).includes(field),
        false
    );

    const doUpdatePoints = userPoints ? (numPoints) => setAmountUsed(numPoints) : () => {};

    const getErrorMessage = () => {
        if (showErrorMsg) {
            return errors.lineItemsCount || formatMessage(validationMessages.fixErrors);
        }
        if (errors.canShip && numFetches > 0) {
            return errors.canShip;
        }
        return undefined;
    };

    const errorMessage = getErrorMessage();

    const handleRecipientTypeChange = (recipientType) => {
        setFieldValue(IS_SELF_BUY, recipientType === 'me');
        resetForm({
            values: {
                ...initialResetValues,
                name: values.product.name,
                product: values.product,
                faceplateCode: values.faceplateCode,
                [fieldNames.AMOUNT]: getConstrainedAmount(values.amount, values.product),
                [fieldNames.DELIVERY_METHOD]: values.deliveryMethod,
                [IS_SELF_BUY]: recipientType === 'me'
            }
        });
    };

    const handleFaceplateClick = (faceplateCode) => {
        setSelectedFaceplate(values.product.faceplates.find((fp) => fp.code === faceplateCode));
    };

    const physicalFormProps = {
        errors,
        anyTouched,
        setTouched,
        setFieldValue,
        addressHashKey: values.shippingGroup ? values.shippingGroup.toAddressHash : '',
        isSelfBuy: values.isSelfBuy,
        useCardNameField: true,
        expectAGift: values.expectAGift
    };
    const deliveryDateProps = {
        dateValue: values[DELIVERY_DATE_FIELD],
        error: errors[DELIVERY_DATE_FIELD],
        setFieldValue
    };
    const digitalFormProps = {
        deliveryDateProps,
        isSelfBuy: values.isSelfBuy
    };
    const deliveryMethodSelectionProps = {
        legend: formatMessage(personalizationFormMessages.deliveryMethodLabel),
        digitalLabel: formatMessage(personalizationFormMessages.eGiftTabName),
        physicalLabel: formatMessage(personalizationFormMessages.physicalTabName),
        handleChange: (method) => setDeliveryMethod(method),
        selectedMethod: values.product.deliveryMethod
    };

    const faceplateCarouselProps = {
        faceplates: values.product.faceplates,
        selected: selectedFaceplate,
        handleChange: handleFaceplateClick
    };
    const recipientTypeProps = {
        cssClass: 'personalize-recipient-type',
        name: 'personalize-recipient-type',
        cashbotName,
        defaultValue: values.isSelfBuy ? 'me' : 'friend',
        legend: headerMessage,
        tabItems: [
            { label: formatMessage(personalizationFormMessages.forAFriend, { deliveryMethod }), value: 'friend' },
            { label: formatMessage(personalizationFormMessages.forMe, { deliveryMethod }), value: 'me' }
        ],
        handleSelect: handleRecipientTypeChange
    };
    const buttonProps = {
        type: 'submit',
        btnText: values.id
            ? formatMessage(personalizationFormMessages.saveChangesButtonText)
            : formatMessage(personalizationFormMessages.addToCartButtonText),
        formName: cashbotName,
        isSmall: false,
        cashbotName
    };

    const isSingleDenomination = () =>
        values.product.baseValue === values.product.maxValue ||
        (!!values.product.baseValue && !values.product.maxValue) ||
        (!values.product.baseValue && !!values.product.maxValue);

    const formattedNumberProps = {
        style: 'currency',
        currency: 'USD',
        value: values.product.baseValue || values.product.maxValue,
        className: 'collapsible'
    };

    useEffect(() => {
        const newFaceplate = product.faceplates.find((fp) => fp.code === selectedFaceplate.code);
        productClicked({ product, faceplate: newFaceplate });
    }, []);

    useEffect(() => {
        if (values.product.faceplates.find((fp) => fp.code === selectedFaceplate.code) !== undefined) {
            setSelectedFaceplate(values.product.faceplates.find((fp) => fp.code === selectedFaceplate.code));
        } else {
            resetForm({
                values: {
                    ...initialResetValues,
                    name: values.product.name,
                    product: values.product,
                    faceplateCode: values.product.faceplates[0].code,
                    faceplate: values.product.faceplates[0],
                    [fieldNames.AMOUNT]: getDefaultDenomination(defaultDenomination, values.product),
                    [fieldNames.DELIVERY_METHOD]: values.product.deliveryMethod,
                    [IS_SELF_BUY]: values.isSelfBuy
                }
            });
            setSelectedFaceplate(values.product.faceplates[0]);
        }
    }, [values.product.faceplates]);

    useEffect(() => {
        const newFaceplate = product.faceplates.find((fp) => fp.code === selectedFaceplate.code);
        setFieldValue(fieldNames.FACEPLATE_CODE, newFaceplate.code);
        setFieldValue(fieldNames.FACEPLATE_IMAGE, newFaceplate.imageURL);
        setFieldValue(fieldNames.FACEPLATE_NAME, values.product.name);
        setFieldValue('faceplate', newFaceplate);
        productViewed({product, faceplate: newFaceplate});
    }, [selectedFaceplate]);

    useEffect(() => {
        const newProduct = deliveryMethod === ELECTRONIC_DELIVERY ? values.digitalGiftCard : values.physicalGiftCard;
        resetForm({
            values: {
                ...initialResetValues,
                name: newProduct.name,
                product: newProduct,
                [fieldNames.AMOUNT]: getConstrainedAmount(values.amount, newProduct),
                [fieldNames.DELIVERY_METHOD]: deliveryMethod,
                [IS_SELF_BUY]: values.isSelfBuy
            }
        });
    }, [deliveryMethod]);

    const createNewAddress = (shippingAddr) => {
        
        if (
            shippingAddr.isSelfBuy
                ? true
                : shippingAddr.senderName &&
                  shippingAddr.fullName &&
                  shippingAddr.street1 &&
                  shippingAddr.postalCode &&
                  shippingAddr.city &&
                  shippingAddr.state
        ) {
            return {
                fullName: shippingAddr.fullName,
                street: shippingAddr.street1,
                street1: shippingAddr.street1,
                street2: shippingAddr.street2,
                postalCode: shippingAddr.postalCode,
                city: shippingAddr.city,
                state: shippingAddr.state,
                countryCode: 'US'
            };
        }
        return false;
    };

    const processSubmission = () => {
        doUpdatePoints(values.amount);
        setIsSubmitting(false);
        submitForm();
    };

    const pageHeaderProps = {
        heading: formatMessage(personalizationFormMessages.personalizationHeader, {
            activeProductName: values.product.name,
            precedingArticle: isVowel(values.product.name.charAt(0)) ? 'an' : 'a'
        })
    };

    useEffect(() => {
        if (shippingMethods.length > 0) {
            setFieldValue('canShip', true);
        }
    }, [shippingMethods, error]);

    useEffect(() => {
        if (values.canShip && isSubmitting) {
            processSubmission();
        }
    }, [values, isSubmitting]);

    const handleSubmit = (event) => {
        setIsSubmitting(true);
        event.preventDefault();
        if (deliveryMethod === 'DIGITAL') {
            processSubmission();
        } else {
            const hasAddress = getHashedAddresses[getAddressHash(values)];
            const newAddress = createNewAddress(values);
            if (hasAddress && newAddress) {
                setFieldValue('canShip', true);
            } else if (newAddress) {
                doFetch(newAddress, values.quantity);
            } else {
                processSubmission();
            }
        }
    };

    return (
        <div className="mall-box">
            <Form onSubmit={handleSubmit}>
                <div className="column-wrap">
                    <div className="primary-column card-column">
                        {displayProductTitle && <PageHeader {...pageHeaderProps} />}
                        {values.digitalGiftCard && values.physicalGiftCard && (
                            <FieldLayoutSmall isCentered>
                                <DeliveryMethodSelection {...deliveryMethodSelectionProps} />
                            </FieldLayoutSmall>
                        )}
                        {product.loyalty_text && (
                            <div className="loyalty_message">
                                <FieldLayoutSmall>
                                    <SanitizedHTML html={values.product.loyalty_text} />
                                </FieldLayoutSmall>
                            </div>
                        )}
                        <div className="personalize-selected-faceplate">
                            <FaceplateImg faceplate={selectedFaceplate} />
                        </div>
                        {product.faceplates.length > 1 && <FaceplateCarousel {...faceplateCarouselProps} />}
                        <FieldLayoutSmall isCentered>
                            <div className="amount-open">
                                {isSingleDenomination() ? (
                                    <div className="closed-denomination-value">
                                        <FormattedNumber {...formattedNumberProps} />
                                    </div>
                                ) : (
                                    <AmountField
                                        minValue={parseFloat(values.product.baseValue).toFixed(2)}
                                        maxValue={parseFloat(values.product.maxValue).toFixed(2)}
                                    />
                                )}
                            </div>
                        </FieldLayoutSmall>
                        {values.product.fees.filter((fee) => (
                            Number(fee.amount) !== 0 && fee.name === "plasticActivationFeeAmount"
                        )).map((fee) => (
                            <p className="card-fee-message">
                                <img alt={fee.displayName} src={CARD_FEE_IMAGE} />
                                {formatMessage(personalizationFormMessages.feeAmountText, {feeAmount: fee.amount})}
                            </p>
                        ))}
                        <GiftCardDetails
                            redemptionText={redemptionText}
                            termsAndConditions={termsAndConditions}
                            description={description}
                            className="full-width"
                        />
                    </div>
                    <div className="secondary-column">
                        {values.product.promo && <PromoBlock promo={values.product.promo} />}
                        <div className="secondary-column__container">
                            <FieldLayoutSmall isCentered>
                                <SubHeader heading={headerMessage} />
                                <TabList {...recipientTypeProps} fullWidth />
                                {deliveryMethod === ELECTRONIC_DELIVERY ? (
                                    <DigitalCardForm {...digitalFormProps} />
                                ) : (
                                    <PhysicalCardForm {...physicalFormProps} />
                                )}
                                <QuantityPicker />
                                <ButtonDisplayErrors {...buttonProps}>
                                    {errorMessage && (
                                        <ButtonErrorMessage errorMessage={errorMessage} iconType="error" />
                                    )}
                                </ButtonDisplayErrors>
                                <GiftCardDetails
                                    redemptionText={redemptionText}
                                    termsAndConditions={termsAndConditions}
                                    description={description}
                                    className="mobile"
                                />
                            </FieldLayoutSmall>
                        </div>
                    </div>
                </div>
            </Form>
        </div>
    );
};

InnerGiftCardForm.defaultProps = {
    initialValues: undefined
};

InnerGiftCardForm.propTypes = {
    values: PropTypes.object.isRequired,
    errors: PropTypes.object.isRequired,
    touched: PropTypes.object.isRequired,
    isValid: PropTypes.bool.isRequired,
    setTouched: PropTypes.func.isRequired,
    setFieldValue: PropTypes.func.isRequired,
    resetForm: PropTypes.func.isRequired,
    initialValues: PropTypes.object,
    lineItemsCount: PropTypes.number.isRequired,
    getHashedAddresses: PropTypes.object.isRequired,
    submitForm: PropTypes.func.isRequired
};

export const GiftCardFormik = (props) => {
    const { item, requestedProduct, alternateProduct, doAddLineItemToCart, lineItemsCount, getHashedAddresses } = props;
    const { productAdded } = useSegment();
    const { userData } = useUsers();
    const history = useHistory();
    const { userPoints, defaultDenomination } = useSettings();
    const initialValues = item.product
        ? item
        : {
              name: requestedProduct.name,
              senderName: '',
              recipientName: '',
              recipientEmail: '',
              quantity: 1,
              amount: getDefaultDenomination(defaultDenomination, requestedProduct),
              message: '',
              brandCode: requestedProduct.brandCode,
              faceplateCode: requestedProduct.faceplates[0].code,
              faceplate: requestedProduct.faceplates[0],
              isSelfBuy: false,
              deliveryMethod: requestedProduct.deliveryMethod,
              deliveryDate: startOfToday(),
              fullName: '',
              street1: '',
              street2: '',
              city: '',
              state: '',
              postalCode: '',
              expectAGift: false,
              product: requestedProduct,
              digitalGiftCard:
                  requestedProduct.deliveryMethod === ELECTRONIC_DELIVERY ? requestedProduct : alternateProduct,
              physicalGiftCard:
                  requestedProduct.deliveryMethod === PHYSICAL_DELIVERY ? requestedProduct : alternateProduct,
              lineItemsCount,
              canShip: false,
              userPoints
          };

    const lineItemAddressValues = {
        ...(item.shippingGroup &&
            item.shippingGroup.toAddress && {
                fullName: item.shippingGroup.toAddress.fullName,
                street1: item.shippingGroup.toAddress.street1,
                street2: item.shippingGroup.toAddress.street2,
                city: item.shippingGroup.toAddress.city,
                state: item.shippingGroup.toAddress.state,
                postalCode: item.shippingGroup.toAddress.postalCode
            })
    };

    const validationSchema = getValidationSchema(useFormatMessage(), userData.availableAmount);

    const submitHandler = (values, formikBag) => {
        const { product, faceplate, amount, quantity } = values;
        doAddLineItemToCart(values);
        formikBag.setSubmitting(false);
        productAdded({ product, faceplate, amount, quantity });
        history.push(summaryPath);
    };

        return (
        <Formik
            displayName="GiftCardForm"
            initialValues={{ ...initialValues, ...item, ...lineItemAddressValues }}
            onSubmit={submitHandler}
            validationSchema={validationSchema}
        >
            {(formProps) => (
                <ShippingRatesProvider>
                    <InnerGiftCardForm
                        {...formProps}
                        initialValues={initialValues}
                        resetForm={formProps.resetForm}
                        lineItemsCount={lineItemsCount}
                        getHashedAddresses={getHashedAddresses}
                        doAddLineItemToCart={doAddLineItemToCart}
                    />
                </ShippingRatesProvider>
            )}
        </Formik>
    );
};

GiftCardFormik.defaultProps = {
    item: {},
    requestedProduct: undefined,
    alternateProduct: undefined
};

GiftCardFormik.propTypes = {
    item: PropTypes.object,
    doAddLineItemToCart: PropTypes.func.isRequired,
    lineItemsCount: PropTypes.number.isRequired,
    getHashedAddresses: PropTypes.object.isRequired,
    requestedProduct: PropTypes.object,
    alternateProduct: PropTypes.object
};

const mapStateToProps = (state) => ({
    lineItemsCount: selectLineItems(state).length,
    getHashedAddresses: hashedAddresses(state)
});

const mapDispatchToProps = (dispatch) => ({
    doAddLineItemToCart: (lineItem) => dispatch(saveLineItem(lineItem))
});

export default connect(mapStateToProps, mapDispatchToProps)(GiftCardFormik);
